/**
 * API админки платформы: единственный эндпоинт `GET /api/admin/overview`.
 *
 * Поверх ТОГО ЖЕ `HttpTransport`, что и остальное приложение (см. `cabinet.ts`): заголовок
 * авторизации и разбор ошибок одни на всё. `auth: true` — доступ закрыт подписью и сверкой
 * `tg_id` с `ADMIN_TG_ID` на бэкенде; не-админ получает 403.
 *
 * Формы DTO списаны с `toOverviewDto` в `packages/backend/src/routes/admin.ts` — единственного
 * места, где бэкенд решает, что уходит наружу.
 */

import type { HttpTransport } from './http';
import type { StatsDto, StatsPeriod } from './cabinet';

export type AdminPlan = 'pro' | 'trial' | 'free';

export interface AdminTotalsDto {
  businesses: number;
  published: number;
  clients: number;
  appointments: number;
  completed: number;
  noShow: number;
  pro: number;
  trial: number;
  free: number;
}

export interface AdminFunnelRowDto {
  /** Метка `src_…` из ссылки `t.me/<бот>?start=src_…`. */
  source: string;
  started: number;
  owners: number;
  businesses: number;
  withBooking: number;
}

export interface AdminBusinessRowDto {
  id: string;
  name: string;
  city: string | null;
  type: string;
  isPublished: boolean;
  plan: AdminPlan;
  clients: number;
  appointments: number;
  noShow: number;
  /** ISO 8601 в UTC; `null` — записей ещё нет. */
  lastActivityAt: string | null;
}

export interface AdminOverviewDto {
  totals: AdminTotalsDto;
  funnel: AdminFunnelRowDto[];
  businesses: AdminBusinessRowDto[];
}

export interface AdminApiClient {
  overview(): Promise<AdminOverviewDto>;
  /** Срез по времени конкретного бизнеса — та же форма, что `cabinet.getStats`, но по любому. */
  businessStats(businessId: string, period: StatsPeriod): Promise<StatsDto>;
}

export function createAdminApi(transport: HttpTransport): AdminApiClient {
  return {
    overview: () =>
      transport.request<AdminOverviewDto>({ method: 'GET', path: '/admin/overview', auth: true }),
    businessStats: (businessId, period) =>
      transport.request<StatsDto>({
        method: 'GET',
        path: `/admin/businesses/${encodeURIComponent(businessId)}/stats`,
        query: { period },
        auth: true,
      }),
  };
}
